/**
 * StakeholderRepo sobre Supabase. Misma lógica que DexieStakeholderRepo.
 * Cuadrante y gap son derivados pero se persisten (igual que en Dexie).
 */

import type { StakeholderRepo } from "../../repos/StakeholderRepo";
import type {
  AjusteAfeStakeholder,
  Stakeholder,
  CuadranteStakeholder,
  NivelEngagement,
} from "@app-types/entities";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

const ENGAGEMENT_RANK: Record<NivelEngagement, number> = {
  BLOQUEA: 0,
  RESISTENTE: 1,
  NEUTRAL: 2,
  APOYA: 3,
  LIDERA: 4,
};

function calcularCuadrante(poder: number, interes: number): CuadranteStakeholder {
  const pAlto = poder >= 4;
  const iAlto = interes >= 4;
  if (pAlto && iAlto) return "GESTIONAR_DE_CERCA";
  if (pAlto && !iAlto) return "MANTENER_SATISFECHO";
  if (!pAlto && iAlto) return "MANTENER_INFORMADO";
  return "MONITOREAR";
}

function calcularGap(actual: NivelEngagement, deseado: NivelEngagement): number {
  return ENGAGEMENT_RANK[deseado] - ENGAGEMENT_RANK[actual];
}

export class SupabaseStakeholderRepo
  extends SupabaseBaseRepo<Stakeholder>
  implements StakeholderRepo
{
  constructor(tenantId: string) {
    super("stakeholder", tenantId);
  }

  async list(filter?: { afeId?: string; estado?: "ACTIVO" | "INACTIVO" }): Promise<Stakeholder[]> {
    let rows = await this.listAll();
    if (filter?.afeId) rows = rows.filter((s) => s.pinneadoAfeIds.includes(filter.afeId!));
    if (filter?.estado) rows = rows.filter((s) => s.estado === filter.estado);
    return rows.sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }));
  }

  async get(id: string): Promise<Stakeholder | null> {
    return this.getById(id);
  }

  async listByAfe(afeId: string): Promise<Stakeholder[]> {
    return this.list({ afeId });
  }

  async create(
    data: Omit<
      Stakeholder,
      "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "codigo" | "cuadrante" | "gap" | "createdBy" | "updatedBy"
    >,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const codigo = await this.siguienteCodigo();
    const nuevo: Stakeholder = {
      ...data,
      id,
      tenantId: this.tenantId,
      codigo,
      cuadrante: calcularCuadrante(data.poder, data.interes),
      gap: calcularGap(data.engagementActual, data.engagementDeseado),
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
      historialEngagement: [
        { fecha: now, usuario: userEmail, cambio: "CREADO", engagementNuevo: data.engagementActual },
      ],
    };
    await this.createRaw(nuevo);
    return id;
  }

  async update(id: string, data: Partial<Stakeholder>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Stakeholder ${id} no encontrado`);
    const now = new Date().toISOString();
    const poder = data.poder ?? current.poder;
    const interes = data.interes ?? current.interes;
    const engagementActual = data.engagementActual ?? current.engagementActual;
    const engagementDeseado = data.engagementDeseado ?? current.engagementDeseado;
    const historial = [...(current.historialEngagement ?? [])];
    if (engagementActual !== current.engagementActual) {
      historial.push({
        fecha: now,
        usuario: current.updatedBy,
        cambio: "ENGAGEMENT_CAMBIO",
        engagementAnterior: current.engagementActual,
        engagementNuevo: engagementActual,
      });
    }
    if (poder !== current.poder || interes !== current.interes) {
      historial.push({ fecha: now, usuario: current.updatedBy, cambio: "PODER_INTERES_CAMBIO" });
    }
    const updated: Stakeholder = {
      ...current,
      ...data,
      poder,
      interes,
      engagementActual,
      engagementDeseado,
      cuadrante: calcularCuadrante(poder, interes),
      gap: calcularGap(engagementActual, engagementDeseado),
      historialEngagement: historial,
      version: current.version + 1,
      updatedAt: now,
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async pinAfe(id: string, afeId: string, userEmail: string): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Stakeholder ${id} no encontrado`);
    if (current.pinneadoAfeIds.includes(afeId)) return;
    await this.putRaw({
      ...current,
      pinneadoAfeIds: [...current.pinneadoAfeIds, afeId],
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
      updatedBy: userEmail,
    });
  }

  async unpinAfe(id: string, afeId: string, userEmail: string): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Stakeholder ${id} no encontrado`);
    if (!current.pinneadoAfeIds.includes(afeId)) return;
    await this.putRaw({
      ...current,
      pinneadoAfeIds: current.pinneadoAfeIds.filter((a) => a !== afeId),
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
      updatedBy: userEmail,
    });
  }

  async setAjusteAfe(id: string, afeId: string, ajuste: AjusteAfeStakeholder, userEmail: string): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Stakeholder ${id} no encontrado`);
    const limpio: AjusteAfeStakeholder = {};
    for (const k of Object.keys(ajuste) as Array<keyof AjusteAfeStakeholder>) {
      const v = ajuste[k];
      if (v === undefined) continue;
      if (typeof v === "string" && v === "") continue;
      (limpio as Record<string, unknown>)[k] = v;
    }
    const prev = current.ajustesPorAfe ?? {};
    const nextAjustes: Record<string, AjusteAfeStakeholder> = { ...prev };
    if (Object.keys(limpio).length === 0) delete nextAjustes[afeId];
    else nextAjustes[afeId] = limpio;
    const pinneado = current.pinneadoAfeIds.includes(afeId)
      ? current.pinneadoAfeIds
      : [...current.pinneadoAfeIds, afeId];
    await this.putRaw({
      ...current,
      ajustesPorAfe: nextAjustes,
      pinneadoAfeIds: pinneado,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
      updatedBy: userEmail,
    });
  }

  async removeAjusteAfe(id: string, afeId: string, userEmail: string): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Stakeholder ${id} no encontrado`);
    const prev = current.ajustesPorAfe ?? {};
    if (!(afeId in prev)) return;
    const nextAjustes: Record<string, AjusteAfeStakeholder> = { ...prev };
    delete nextAjustes[afeId];
    await this.putRaw({
      ...current,
      ajustesPorAfe: nextAjustes,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
      updatedBy: userEmail,
    });
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  private async siguienteCodigo(): Promise<string> {
    const todos = await this.list();
    const max = todos
      .map((s) => parseInt(s.codigo.replace(/^STK-/, ""), 10))
      .filter((n) => !isNaN(n))
      .reduce((m, n) => Math.max(m, n), 0);
    return `STK-${String(max + 1).padStart(3, "0")}`;
  }
}
